import React, { useEffect } from 'react';
import { View, Text } from 'react-native';
import Input from '@/components/General/inputs/Input';
import { saveCalendar } from "@/data/calendars";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import InputSection from '@/components/General/inputs/InputSection';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import Toast from 'react-native-toast-message'
import { redirectToDefault } from '@/utils/constants/linking';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import { useLocalSearchParams, usePathname, useRouter } from 'expo-router';
import ModalWrapper from '@/components/General/ModalWrapper';
import ColorPicker, { Panel1, Preview, HueSlider } from 'reanimated-color-picker';
import usePlatform from '@/utils/hooks/usePlatform';
import { areStringsPopulated } from '@/utils/InputValidator';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { StyleSheet } from 'react-native-unistyles';

const EMPTY_DRAFT = {};

export default function AddCalendarModal() {
    const {id, fromScreen, fromStoreKey} = useLocalSearchParams();
    const storeKey = id || "new"

    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData)
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData)
    const calendarDetails = useEditScreenStore(s => s.AddCalendarModal?.[storeKey]) || EMPTY_DRAFT

    const {isNative} = usePlatform()
    const queryClient = useQueryClient()
    const router = useRouter()
    const pathname = usePathname()

    const { data: loggedInUser } = Query.people.loggedInUser()
    const { data: calendarToEdit } = Query.calendars.byId({id: id, options: {enabled: !!id, placeholderData: {}}})
    

    const saveCalendarMutation = useMutation({
        mutationFn: saveCalendar,
        onSuccess: (data) => {
            removeEditScreenData("AddCalendarModal", storeKey)
            queryClient.invalidateQueries({queryKey: QueryKeys.events.all()});
            queryClient.invalidateQueries({queryKey: QueryKeys.calendars.all()})

            Toast.delayShow({ type: 'success', text2: `New calendar has been ${id ? "updated" : "added"} successfully!`});

            if(fromScreen){
                useEditScreenStore.getState().dataChangeFunction?.(data)
                router.dismissTo({pathname:fromScreen, params: fromStoreKey && fromStoreKey != "new" ? {id: fromStoreKey} : undefined});
            }
            else{
                redirectToDefault(router, "AddCalendarModal")
            }
        },
        onError: () => {
            console.debug("errored")
        },
    })




    /*** Effects ***/
    useEffect(() => {
        if(calendarToEdit){
            setCalendarDetails({
                ...calendarToEdit,
                originalValues: calendarToEdit,
            })
        }
    }, [calendarToEdit]);


    const setCalendarDetails = (details) => {
        setEditScreenData("AddCalendarModal", storeKey, details)
    }

    return (
        <ModalWrapper
            loading={saveCalendarMutation.isPending} 
            onCancel={() => removeEditScreenData("AddCalendarModal", storeKey)} 
            onConfirm={() => {
                if(!areStringsPopulated([calendarDetails.name])){
                    Toast.show({ type: 'error', text2: "Your calendar must have a name." });
                    return;
                }

                const { originalValues, ...cleanDetails } = calendarDetails;
                saveCalendarMutation.mutate({...cleanDetails, originalValues, createdByID: loggedInUser?.id})
            }} 
            title={id ? "Update Calendar" : "New Calendar"}
        >
            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled" contentContainerStyle={[styles.scrollContent, isNative && styles.scrollContentNative]} >
                {/* <InputSection description="This calendar will give you the ability to role similar events. Events in specific calendars will be indicated by the given color."> */}
                    <Input
                        required
                        isNotOnSurface
                        title="Name"
                        maxLength={120}
                        value={calendarDetails.name}
                        onUpdate={(new_value) => {
                            setCalendarDetails({
                                name: new_value
                            })
                        }}
                    />

                    <Input
                        isNotOnSurface
                        title="Description"
                        value={calendarDetails.description}
                        multiline
                        onUpdate={(new_value) => {
                            setCalendarDetails({
                                description: new_value
                            })
                        }}
                    />

                    <InputSection title="Roles" description="The people in the chosen roles will be able to see the events in this calendar. If no roles are chosen, everyone will be able to see this calendar.">
                        <SheetSelector multiple type="roles" title="Roles" fromScreen={pathname} fromStoreKey={storeKey} value={calendarDetails.Roles || []} onChange={(selected_items) => {
                            setCalendarDetails({
                                Roles: selected_items
                            })
                        }}/>
                        {(!calendarDetails.Roles || calendarDetails.Roles.length === 0) && (
                            <Text style={styles.rolesHint}>
                                Since no roles are chosen, everyone in your organization will be able to see events in this calendar.
                            </Text>
                        )}
                    </InputSection>

                    <ColorPicker style={styles.colorPicker} sliderThickness={10} thumbSize={20} thumbShape="solid" value={calendarDetails.color || undefined} onCompleteJS={({hex}) => setCalendarDetails({color: hex})}>
                        <View style={styles.previewRow}>
                            <Text style={styles.colorPickerLabel}>Calendar Color</Text>
                            <Preview style={styles.preview} hideInitialColor/>
                        </View>
                        <View style={styles.sliderContainer}>
                            <HueSlider boundedThumb thumbColor="#ffffff" />
                        </View>
                        <View style={styles.sliderContainer}>
                            <Panel1  thumbSize={15}  thumbShape="ring" thumbStyle={styles.panelThumb} />
                        </View>
                    </ColorPicker>

            </KeyboardAwareScrollView>
        </ModalWrapper>
    );
}

const styles = StyleSheet.create((theme) => ({
    scrollContent: { paddingBottom: 0 },
    scrollContentNative: { paddingBottom: 100 },
    rolesHint: { fontSize: 12, color: theme.colors.warning, fontStyle: 'italic', marginTop: 8 },
    colorPicker: { width: '100%', padding: 10 },
    previewRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 10, width: '100%', marginTop: 20 },
    colorPickerLabel: { fontSize: 16, fontWeight: '600', color: theme.colors.text },
    preview: { width: 150 },
    sliderContainer: { paddingVertical: 10 },
    panelThumb: { borderWidth: 3, outline: 'none', boxShadow: 'none' },
}));